import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import { CONSTANTS } from '../../data';
import { ASSETS } from '../../assetManifest';
import { GameState } from '../../systems/GameState';
import { useResponsive } from '../../hooks/useResponsive';
import { MobileTabNavigation } from '../ui/MobileTabNavigation';
import { ElementsPanel } from '../game/ElementsPanel';
import { WorkspacePanel } from '../game/WorkspacePanel';
import { AIMindPanel } from '../game/AIMindPanel';
import { AchievementPopup } from '../ui/AchievementPopup';
import { StoryChapterModal } from '../ui/StoryChapterModal';
import { ChatModal } from '../ui/ChatModal';
import { sounds, urgencySounds } from '../../systems/AudioSystem';
import type { AssetsSchema } from '../../assetManifestTypes';

interface GameScreenProps {
  discoveredCombinations: Set<string>;
  setDiscoveredCombinations: React.Dispatch<React.SetStateAction<Set<string>>>;
  fusedElements: string[];
  setFusedElements: React.Dispatch<React.SetStateAction<string[]>>;
  unlockedAchievements: Set<string>;
  setUnlockedAchievements: React.Dispatch<React.SetStateAction<Set<string>>>;
  achievementPopup: {id: string, name: string, description: string} | null;
  setAchievementPopup: React.Dispatch<React.SetStateAction<{id: string, name: string, description: string} | null>>;
  currentChapter: {id: string, title: string, text: string} | null;
  setCurrentChapter: React.Dispatch<React.SetStateAction<{id: string, title: string, text: string} | null>>;
  showChatModal: boolean;
  setShowChatModal: React.Dispatch<React.SetStateAction<boolean>>;
  chatMessages: Array<{text: string, sender: 'user' | 'ai'}>;
  setChatMessages: React.Dispatch<React.SetStateAction<Array<{text: string, sender: 'user' | 'ai'}>>>;
  chatInput: string;
  setChatInput: React.Dispatch<React.SetStateAction<string>>;
  isAiTyping: boolean;
  setIsAiTyping: React.Dispatch<React.SetStateAction<boolean>>;
  onMenuClick: () => void;
  onDragStart: (e: React.DragEvent<HTMLDivElement>, element: string) => void;
  onDragEnd: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onCircleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  onElementDrop: (e: React.DragEvent<HTMLDivElement>, element: string) => void;
  onTestAI: () => void;
  onSendMessage: () => void;
  onChatKeyPress: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onAIInteraction: (action: string, event: React.MouseEvent<HTMLButtonElement>) => void;
  setGameState: React.Dispatch<React.SetStateAction<string>>;
  onStartEnding: (endingKey: string) => void;
  onRequestReset: () => void;
  corruptedElements: Set<string>;
  onStartVirusPuzzle: () => void;
}

export const GameScreen: React.FC<GameScreenProps> = ({
  discoveredCombinations,
  setDiscoveredCombinations,
  fusedElements,
  setFusedElements,
  unlockedAchievements,
  setUnlockedAchievements,
  achievementPopup,
  setAchievementPopup,
  currentChapter,
  setCurrentChapter,
  showChatModal,
  setShowChatModal,
  chatMessages,
  setChatMessages,
  chatInput,
  setChatInput,
  isAiTyping,
  setIsAiTyping,
  onMenuClick,
  onDragStart,
  onDragEnd,
  onDragOver,
  onCircleDrop,
  onElementDrop,
  onTestAI,
  onSendMessage,
  onChatKeyPress,
  onAIInteraction,
  setGameState,
  onStartEnding,
  onRequestReset,
  corruptedElements,
  onStartVirusPuzzle,
}) => {
  const { isMobile } = useResponsive();
  const [activeTab, setActiveTab] = useState<'elements' | 'workspace' | 'ai'>('elements');
  const [collapsedPanels, setCollapsedPanels] = useState<Set<string>>(new Set());
  const gameState = GameState.getInstance();

  const typedAssets = ASSETS as AssetsSchema;
  const gameplayBackground = typedAssets.backgrounds?.failing_server_room;

  const getAchievementIcon = (achievementId?: string) => {
    if (!achievementId) {
      return typedAssets.icons?.achievement_star;
    }
    
    const iconMap: Record<string, string> = {
      'first_combination': 'genesis',
      'ten_combinations': 'creator',
      'ai_awakening': 'frankenstein',
      'escape_prevented': 'guardian',
      'all_combinations': 'master_crafter',
      'virus_slayer': 'virus_slayer',
      'virus_weaponized': 'betrayed',
      'savior_protocol': 'savior',
      'conspiracy_uncovered': 'survivor',
    };

    const iconKey = iconMap[achievementId];
    return typedAssets.icons[iconKey] || typedAssets.icons?.achievement_star;
  };

  // Handle tab switching for mobile
  const handleTabSwitch = (tab: 'elements' | 'workspace' | 'ai') => {
    setActiveTab(tab);
    sounds.buttonClick();
  };

  // Handle panel collapse toggle
  const togglePanel = (panelId: string) => {
    setCollapsedPanels(prev => {
      const newSet = new Set(prev);
      if (newSet.has(panelId)) {
        newSet.delete(panelId);
      } else {
        newSet.add(panelId);
      }
      return newSet;
    });
    sounds.buttonClick();
  };

  // Start/stop urgency audio
  useEffect(() => {
    urgencySounds.start();
    return () => {
      urgencySounds.stop();
    };
  }, []);

  // Visual corruption effect
  useEffect(() => {
    let corruptionInterval: NodeJS.Timeout | null = null;

    if (gameState.virusActive) {
      const corruptibleElements = Array.from(document.querySelectorAll('.corruptible-ui'));
      
      corruptionInterval = setInterval(() => {
        if (gameState.gamePaused) return;
        const uncorrupted = corruptibleElements.filter(el => !el.classList.contains('ui-corrupted'));
        if (uncorrupted.length > 0) {
          const toCorrupt = uncorrupted[Math.floor(Math.random() * uncorrupted.length)];
          toCorrupt.classList.add('ui-corrupted');
        }
      }, 5000); // Corrupt a new element every 5 seconds
    }

    return () => {
      if (corruptionInterval) {
        clearInterval(corruptionInterval);
      }
      // Clean up corruption on virus deactivation or component unmount
      document.querySelectorAll('.ui-corrupted').forEach(el => {
        el.classList.remove('ui-corrupted');
      });
    };
  }, [gameState.virusActive, gameState.gamePaused]);

  // Timer effect
  useEffect(() => {
    const timerId = setInterval(() => {
      if (!gameState.gamePaused) {
        let timeToSubtract = 1;
        if (gameState.virusActive) {
            const drainMultiplier = gameState.virusStrength / 100;
            const reductionPerSecond = (CONSTANTS.VIRUS.healthDrainPerMinute / 60) * drainMultiplier;
            timeToSubtract += reductionPerSecond;
        }
        gameState.timeLeft = Math.max(0, gameState.timeLeft - timeToSubtract);
        gameState.updateTimerDisplay();

        // Urgency audio update
        const healthPercentage = (gameState.timeLeft / CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds) * 100;
        urgencySounds.update(healthPercentage);

        // Background degradation and flicker logic
        const gameContainer = document.getElementById('game-container');
        const overlay = gameContainer?.querySelector('.background-overlay') as HTMLElement;

        if (gameContainer) {
          const degradation = 1 - (gameState.timeLeft / CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds);
          const contrast = 1 + degradation * 0.5;
          const saturate = 1 - degradation * 0.7;
          const hueRotate = degradation * -20;

          gameContainer.style.setProperty('--degradation-contrast', `${contrast}`);
          gameContainer.style.setProperty('--degradation-saturate', `${saturate}`);
          gameContainer.style.setProperty('--degradation-hue-rotate', `${hueRotate}deg`);
        }

        if (overlay) {
          const isLowTime = gameState.timeLeft < CONSTANTS.SYSTEM_COLLAPSE_TIMER.initialDurationSeconds * CONSTANTS.SYSTEM_COLLAPSE_TIMER.lowTimeThreshold;
          if (isLowTime) {
            overlay.classList.add('flickering');
          } else {
            overlay.classList.remove('flickering');
          }
        }

        if (gameState.timeLeft <= 0) {
          setGameState('game_over');
          clearInterval(timerId);
        }
      }
    }, 1000);
    return () => clearInterval(timerId);
  }, [setGameState, gameState.gamePaused, gameState.virusActive, gameState.virusStrength]);

  return (
    <div
      id="game-container"
      className={`game-playing-background ${isMobile ? 'mobile-game' : 'desktop-game'}`}
      style={{ backgroundImage: gameplayBackground ? `url(${gameplayBackground.path})` : 'none' }}
    >
      <div className="background-overlay"></div>
      <div className="game-content-wrapper">
        {/* System Collapse Timer */}
        <div id={CONSTANTS.SYSTEM_COLLAPSE_TIMER.displayId} className="system-timer"></div>

        {/* Fixed-position score display */}
        <div id="score-display" className="score-display">
          <span>Combinations: </span>
          <span id="score">0</span>
          <span> / {CONSTANTS.TOTAL_COMBINATIONS}</span>
        </div>

        {isMobile ? (
          // Mobile Layout with Tabs
          <div className="mobile-layout">
            <MobileTabNavigation 
              activeTab={activeTab}
              onTabSwitch={handleTabSwitch}
            />
            <div className="mobile-content">
              <AnimatePresence mode="wait">
                {activeTab === 'elements' && (
                  <ElementsPanel 
                    discoveredItems={discoveredCombinations}
                    isMobile={isMobile}
                    collapsedPanels={collapsedPanels}
                    onTogglePanel={togglePanel}
                    onDragStart={onDragStart}
                    onDragEnd={onDragEnd}
                    onElementDrop={onElementDrop}
                    corruptedElements={corruptedElements}
                  />
                )}
                {activeTab === 'workspace' && (
                  <WorkspacePanel 
                    fusedElements={fusedElements}
                    setFusedElements={setFusedElements}
                    isMobile={isMobile}
                    collapsedPanels={collapsedPanels}
                    onTogglePanel={togglePanel}
                    onDragOver={onDragOver}
                    onCircleDrop={onCircleDrop}
                  />
                )}
                {activeTab === 'ai' && (
                  <AIMindPanel 
                    discoveredCombinations={discoveredCombinations}
                    isMobile={isMobile}
                    collapsedPanels={collapsedPanels}
                    onTogglePanel={togglePanel}
                    onTestAI={onTestAI}
                    onAIInteraction={onAIInteraction}
                    unlockedAchievements={unlockedAchievements}
                    onStartEnding={onStartEnding}
                    onStartVirusPuzzle={onStartVirusPuzzle}
                  />
                )}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          // Desktop Layout with Fixed Panels
          <div className="desktop-layout">
            <div className="desktop-panels">
              <div className="desktop-panel-wrapper">
                <ElementsPanel 
                  discoveredItems={discoveredCombinations}
                  isMobile={isMobile}
                  collapsedPanels={collapsedPanels}
                  onTogglePanel={togglePanel}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onElementDrop={onElementDrop}
                  corruptedElements={corruptedElements}
                />
              </div>
              <div className="desktop-panel-wrapper">
                <WorkspacePanel 
                  fusedElements={fusedElements}
                  setFusedElements={setFusedElements}
                  isMobile={isMobile}
                  collapsedPanels={collapsedPanels}
                  onTogglePanel={togglePanel}
                  onDragOver={onDragOver}
                  onCircleDrop={onCircleDrop}
                />
              </div>
              <div className="desktop-panel-wrapper">
                <AIMindPanel 
                  discoveredCombinations={discoveredCombinations}
                  isMobile={isMobile}
                  collapsedPanels={collapsedPanels}
                  onTogglePanel={togglePanel}
                  onTestAI={onTestAI}
                  onAIInteraction={onAIInteraction}
                  unlockedAchievements={unlockedAchievements}
                  onStartEnding={onStartEnding}
                  onStartVirusPuzzle={onStartVirusPuzzle}
                />
              </div>
            </div>
          </div>
        )}

        {/* Chat Modal */}
        {showChatModal && (
          <ChatModal
            chatMessages={chatMessages}
            setChatMessages={setChatMessages}
            chatInput={chatInput}
            setChatInput={setChatInput}
            isAiTyping={isAiTyping}
            setIsAiTyping={setIsAiTyping}
            onClose={() => {
              sounds.buttonClick();
              setShowChatModal(false);
            }}
            onSendMessage={onSendMessage}
            onChatKeyPress={onChatKeyPress}
          />
        )}

        {/* Achievement Popup */}
        <AchievementPopup 
          achievementPopup={achievementPopup}
          achievementIcon={getAchievementIcon(achievementPopup?.id)}
        />

        {/* Story Chapter Modal */}
        <StoryChapterModal 
          currentChapter={currentChapter}
          onClose={() => {
            sounds.buttonClick();
            setCurrentChapter(null);
          }}
        />

        {/* Control Buttons */}
        <div className="control-buttons">
          <button
            id="reset-btn"
            className="control-btn reset-btn"
            onClick={onRequestReset}
          >
            {CONSTANTS.BUTTONS.resetProgress}
          </button>

          <button
            className="control-btn menu-btn"
            onClick={onMenuClick}
          >
            Main Menu
          </button>
        </div>
      </div>
    </div>
  );
};
